import { STEEL_CATEGORY } from '../../constants';
import { SteelCategory } from '../../types/common';
import { OptionsType } from '../../types/options';

/**
 * URL Search Paramsから分類のクエリ（品種: or 品種_EN:）を探して返す・
 * ・分類：条鋼　＝　品種:(線材 OR 棒鋼) or 品種_EN:(BAR OR WIRE_ROD)
 * ・分類：鋼板　＝　品種:鋼板 or 品種_EN:(STEEL_PLATE OR STEEL_SHEET)
 * ・分類：鋼管　＝　品種:鋼管 or 品種_EN:PIPE
 * ※複数の値の組み合わせ時は、順不同かつ重複可能
 * @param q: [string]
 * @returns [SteelCategory | undefined]
 */
const getSteelCategoryInSearchParams = (q: string, steelCategoryType: OptionsType<SteelCategory, string>[]): SteelCategory | undefined => {
  const queryItems: string[] = q.split(' AND ');
  const steelTypeQueryJa: string | undefined = queryItems.find((query) => query.indexOf('品種:') > -1);
  const steelTypeQueryEn: string | undefined = queryItems.find((query) => query.indexOf('品種_EN:') > -1);
  const queryValueJa: string | undefined = steelTypeQueryJa ? steelTypeQueryJa.split('品種:')[1] : undefined;
  const queryValueEn: string | undefined = steelTypeQueryEn ? steelTypeQueryEn.split('品種_EN:')[1] : undefined;
  // console.log(queryItems, steelTypeQueryJa, steelTypeQueryEn, queryValueJa, queryValueEn);

  const barWireRodChildren = steelCategoryType.find((option) => option.code === STEEL_CATEGORY.BAR)?.children ?? [];
  const sheetSteelChildren = steelCategoryType.find((option) => option.code === STEEL_CATEGORY.PLATE)?.children ?? [];
  const pipeChildren = steelCategoryType.find((option) => option.code === STEEL_CATEGORY.PIPE)?.children ?? [];

  // 複数指定の判定のため、半角丸カッコで囲まれた中身も取得しておく
  // ----------------------------------------
  // 半角丸カッコで囲まれた中身をヒットさせる正規表現をカスタマイズ
  // https://www-creators.com/archives/5462
  const regexp = /(?<=^\().*?(?=\)$)/;
  // カッコで囲まれた中身（matchはArrayを返す）をゲットする
  // 例）['線材|棒鋼']
  // 複数指定でない場合は空の配列になる。
  const innerValueArrayJa: string[] = queryValueJa ? queryValueJa.match(regexp) ?? [] : [];
  const innerValueArrayEn: string[] = queryValueEn ? queryValueEn.match(regexp) ?? [] : [];

  // 条鋼は下記のケース：
  // ========================================
  // ・品種が複数指定されており、かつその要素が「線材」「棒鋼」である。
  // ・品種_ENが複数指定されており、かつその要素が「BAR」「WIRE_ROD」である。
  // ただし複数指定時のセパレータは「 OR 」か「 or 」か「|」であり、要素は重複している可能性もある。
  const isBarWireRod: boolean = (() => {
    if (innerValueArrayJa.length === 1) {
      // 品種の場合
      // ----------------------------------------
      // 複数指定された値が「線材」か「棒鋼」のいずれかだけであることを確認する
      const queryElements: string[] = barWireRodChildren.map((child) => child.nameJapanese);
      // console.log(queryElements);
      const innerValue: string = innerValueArrayJa[0];
      // 「 OR 」セパレータの場合
      // ----------
      const valuesByUpperOr: string[] = innerValue.split(' OR ');
      // 重複をなくす
      const elementsByUpperOr: string[] = Array.from(new Set(valuesByUpperOr));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByUpperOr: boolean = queryElements.filter((element) => elementsByUpperOr.indexOf(element) === -1).length === 0;
      // 「 or 」セパレータの場合
      // ----------
      const valuesByLowerOr: string[] = innerValue.split(' or ');
      // 重複をなくす
      const elementsByLowerOr: string[] = Array.from(new Set(valuesByLowerOr));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByLowerOr: boolean = queryElements.filter((element) => elementsByLowerOr.indexOf(element) === -1).length === 0;
      // 「|」セパレータの場合
      // ----------
      const valuesByVerticalBar: string[] = innerValue.split('|');
      // 重複をなくす
      const elementsByVerticalBar: string[] = Array.from(new Set(valuesByVerticalBar));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByVerticalBar: boolean = queryElements.filter((element) => elementsByVerticalBar.indexOf(element) === -1).length === 0;
      // console.log(elementsByUpperOr, elementsByLowerOr, elementsByVerticalBar);
      // ３つのセパレータのいずれかでvalidなクエリが見つかったら条鋼
      if (isValidByUpperOr || isValidByLowerOr || isValidByVerticalBar) {
        return true;
      } else {
        return false;
      }
    } else if (innerValueArrayEn.length === 1) {
      // 品種_ENの場合
      // ----------------------------------------
      // 複数指定された値が「線材」か「棒鋼」のいずれかだけであることを確認する
      const queryElements: string[] = barWireRodChildren.map((child) => child.nameEnglish);
      // console.log(queryElements);
      const innerValue: string = innerValueArrayEn[0];
      // 「 OR 」セパレータの場合
      // ----------
      const valuesByUpperOr: string[] = innerValue.split(' OR ');
      // 重複をなくす
      const elementsByUpperOr: string[] = Array.from(new Set(valuesByUpperOr));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByUpperOr: boolean = queryElements.filter((element) => elementsByUpperOr.indexOf(element) === -1).length === 0;
      // 「 or 」セパレータの場合
      // ----------
      const valuesByLowerOr: string[] = innerValue.split(' or ');
      // 重複をなくす
      const elementsByLowerOr: string[] = Array.from(new Set(valuesByLowerOr));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByLowerOr: boolean = queryElements.filter((element) => elementsByLowerOr.indexOf(element) === -1).length === 0;
      // 「|」セパレータの場合
      // ----------
      const valuesByVerticalBar: string[] = innerValue.split('|');
      // 重複をなくす
      const elementsByVerticalBar: string[] = Array.from(new Set(valuesByVerticalBar));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByVerticalBar: boolean = queryElements.filter((element) => elementsByVerticalBar.indexOf(element) === -1).length === 0;
      // console.log(elementsByUpperOr, elementsByLowerOr, elementsByVerticalBar);
      // ３つのセパレータのいずれかでvalidなクエリが見つかったら条鋼
      if (isValidByUpperOr || isValidByLowerOr || isValidByVerticalBar) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  })();

  // 鋼板は下記のケース：
  // ========================================
  // ・品種が複数指定されており、かつその要素が「厚板」「薄板」である。
  // ・品種_ENが複数指定されており、かつその要素が「STEEL_PLATE」「STEEL_SHEET」である。
  // ただし複数指定時のセパレータは「 OR 」か「 or 」か「|」であり、要素は重複している可能性もある。
  const isSheetSteel: boolean = (() => {
    if (innerValueArrayJa.length === 1) {
      // 品種の場合
      // ----------------------------------------
      // 複数指定された値が「厚板」「薄板」のいずれかだけであることを確認する
      const queryElements: string[] = sheetSteelChildren.map((child) => child.nameJapanese);
      // console.log(queryElements);
      const innerValue: string = innerValueArrayJa[0];
      // 「 OR 」セパレータの場合
      // ----------
      const valuesByUpperOr: string[] = innerValue.split(' OR ');
      // 重複をなくす
      const elementsByUpperOr: string[] = Array.from(new Set(valuesByUpperOr));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByUpperOr: boolean = queryElements.filter((element) => elementsByUpperOr.indexOf(element) === -1).length === 0;
      // 「 or 」セパレータの場合
      // ----------
      const valuesByLowerOr: string[] = innerValue.split(' or ');
      // 重複をなくす
      const elementsByLowerOr: string[] = Array.from(new Set(valuesByLowerOr));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByLowerOr: boolean = queryElements.filter((element) => elementsByLowerOr.indexOf(element) === -1).length === 0;
      // 「|」セパレータの場合
      // ----------
      const valuesByVerticalBar: string[] = innerValue.split('|');
      // 重複をなくす
      const elementsByVerticalBar: string[] = Array.from(new Set(valuesByVerticalBar));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByVerticalBar: boolean = queryElements.filter((element) => elementsByVerticalBar.indexOf(element) === -1).length === 0;
      // console.log(elementsByUpperOr, elementsByLowerOr, elementsByVerticalBar);
      // ３つのセパレータのいずれかでvalidなクエリが見つかったら条鋼
      if (isValidByUpperOr || isValidByLowerOr || isValidByVerticalBar) {
        return true;
      } else {
        return false;
      }
    } else if (innerValueArrayEn.length === 1) {
      // 品種_ENの場合
      // ----------------------------------------
      // 複数指定された値が「STEEL_PLATE」「STEEL_SHEET」のいずれかだけであることを確認する
      const queryElements: string[] = sheetSteelChildren.map((child) => child.nameEnglish);
      // console.log(queryElements);
      const innerValue: string = innerValueArrayEn[0];
      // 「 OR 」セパレータの場合
      // ----------
      const valuesByUpperOr: string[] = innerValue.split(' OR ');
      // 重複をなくす
      const elementsByUpperOr: string[] = Array.from(new Set(valuesByUpperOr));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByUpperOr: boolean = queryElements.filter((element) => elementsByUpperOr.indexOf(element) === -1).length === 0;
      // 「 or 」セパレータの場合
      // ----------
      const valuesByLowerOr: string[] = innerValue.split(' OR ');
      // 重複をなくす
      const elementsByLowerOr: string[] = Array.from(new Set(valuesByLowerOr));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByLowerOr: boolean = queryElements.filter((element) => elementsByLowerOr.indexOf(element) === -1).length === 0;
      // 「|」セパレータの場合
      // ----------
      const valuesByVerticalBar: string[] = innerValue.split(' OR ');
      // 重複をなくす
      const elementsByVerticalBar: string[] = Array.from(new Set(valuesByVerticalBar));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByVerticalBar: boolean = queryElements.filter((element) => elementsByVerticalBar.indexOf(element) === -1).length === 0;
      // console.log(elementsByUpperOr, elementsByLowerOr, elementsByVerticalBar);
      // ３つのセパレータのいずれかでvalidなクエリが見つかったら条鋼
      if (isValidByUpperOr || isValidByLowerOr || isValidByVerticalBar) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  })();

  // 鋼管は下記のケース：
  // ========================================
  // ・品種が「鋼管」である。
  // ・品種_ENが「PIPE」である。
  const isPipe: boolean = (() => {
    if (innerValueArrayJa.length === 1) {
      // 品種の場合
      // ----------------------------------------
      // 複数指定された値が「鋼管」だけであることを確認する
      const queryElements: string[] = pipeChildren.map((child) => child.nameJapanese);
      // console.log(queryElements);
      const innerValue: string = innerValueArrayJa[0];
      // 「 OR 」セパレータの場合
      // ----------
      const valuesByUpperOr: string[] = innerValue.split(' OR ');
      // 重複をなくす
      const elementsByUpperOr: string[] = Array.from(new Set(valuesByUpperOr));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByUpperOr: boolean = queryElements.filter((element) => elementsByUpperOr.indexOf(element) === -1).length === 0;
      // 「 or 」セパレータの場合
      // ----------
      const valuesByLowerOr: string[] = innerValue.split(' or ');
      // 重複をなくす
      const elementsByLowerOr: string[] = Array.from(new Set(valuesByLowerOr));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByLowerOr: boolean = queryElements.filter((element) => elementsByLowerOr.indexOf(element) === -1).length === 0;
      // 「|」セパレータの場合
      // ----------
      const valuesByVerticalBar: string[] = innerValue.split('|');
      // 重複をなくす
      const elementsByVerticalBar: string[] = Array.from(new Set(valuesByVerticalBar));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByVerticalBar: boolean = queryElements.filter((element) => elementsByVerticalBar.indexOf(element) === -1).length === 0;
      // console.log(elementsByUpperOr, elementsByLowerOr, elementsByVerticalBar);
      // ３つのセパレータのいずれかでvalidなクエリが見つかったら条鋼
      if (isValidByUpperOr || isValidByLowerOr || isValidByVerticalBar) {
        return true;
      } else {
        return false;
      }
    } else if (innerValueArrayEn.length === 1) {
      // 品種_ENの場合
      // ----------------------------------------
      // 複数指定された値が「PIPE」だけであることを確認する
      const queryElements: string[] = pipeChildren.map((child) => child.nameEnglish);
      // console.log(queryElements);
      const innerValue: string = innerValueArrayEn[0];
      // 「 OR 」セパレータの場合
      // ----------
      const valuesByUpperOr: string[] = innerValue.split(' OR ');
      // 重複をなくす
      const elementsByUpperOr: string[] = Array.from(new Set(valuesByUpperOr));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByUpperOr: boolean = queryElements.filter((element) => elementsByUpperOr.indexOf(element) === -1).length === 0;
      // 「 or 」セパレータの場合
      // ----------
      const valuesByLowerOr: string[] = innerValue.split(' OR ');
      // 重複をなくす
      const elementsByLowerOr: string[] = Array.from(new Set(valuesByLowerOr));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByLowerOr: boolean = queryElements.filter((element) => elementsByLowerOr.indexOf(element) === -1).length === 0;
      // 「|」セパレータの場合
      // ----------
      const valuesByVerticalBar: string[] = innerValue.split(' OR ');
      // 重複をなくす
      const elementsByVerticalBar: string[] = Array.from(new Set(valuesByVerticalBar));
      // 要素に差分がなければ条鋼のクエリ
      const isValidByVerticalBar: boolean = queryElements.filter((element) => elementsByVerticalBar.indexOf(element) === -1).length === 0;
      // console.log(elementsByUpperOr, elementsByLowerOr, elementsByVerticalBar);
      // ３つのセパレータのいずれかでvalidなクエリが見つかったら条鋼
      if (isValidByUpperOr || isValidByLowerOr || isValidByVerticalBar) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  })();

  if (isBarWireRod) {
    return STEEL_CATEGORY.BAR;
  } else if (isSheetSteel) {
    return STEEL_CATEGORY.PLATE;
  } else if (isPipe) {
    return STEEL_CATEGORY.PIPE;
  } else {
    return undefined;
  }
};

export default getSteelCategoryInSearchParams;
